/**
 * Video + Skeleton synchronized preview — pure geometry/timing core.
 *
 * VISTOERA_MOTION_RECONSTRUCTION_VIDEO_SKELETON_REAL_SOURCE_SYNCHRONIZED_PREVIEW_V2.
 *
 * Two independent pure concerns, kept separate on purpose:
 *  - `resolveContainedMediaRect` — CSS `object-fit: contain` letterbox math
 *    (frontend rendering transform, NOT the PC4 vendor preprocessing
 *    transform of the same name in spirit).
 *  - `findNearestMotionFrameByTime` / `findNearestTimestampIndex` — binary
 *    search over the Motion Asset's `timestamp_ms` samples so a video's
 *    `currentTime` (master clock) resolves to the nearest lower-fps motion
 *    frame in O(log n), never a per-tick O(n) scan.
 *
 * The 2D Motion Asset consumed by the Video + Skeleton overlay
 * (`CanvasMotionReconstruction.input_motion_asset_id`) is already
 * single-track, positional H36M-17, `coordinate_space: camera_normalized`
 * — but for THIS asset that label means "BlazePose-native 0..1, top-left
 * origin, x→right, y→down, relative to the full source video frame"
 * (backend `motion_extraction_execution_payload.convert_canonical_to_h36m_17`,
 * `joint_schema.map_blazepose_landmarks`). That is a direct pixel mapping —
 * no Y-flip — unlike the standalone 3D skeleton-only preview's own virtual
 * camera projection (`motionSkeletonPreviewCore.projectFrameJoints`, which
 * intentionally flips Y for its own unrelated display convention). Do not
 * conflate the two: `projectVideoAlignedJoint` below is deliberately a
 * separate, unflipped function.
 */

export type ContainedMediaRect = {
  renderedWidth: number
  renderedHeight: number
  offsetX: number
  offsetY: number
}

/** CSS `object-fit: contain` box math — the actual rendered media content
 * rect within a container, given the source's intrinsic pixel size. */
export function resolveContainedMediaRect(
  containerWidth: number,
  containerHeight: number,
  sourceWidth: number,
  sourceHeight: number,
): ContainedMediaRect {
  if (
    !Number.isFinite(containerWidth) ||
    !Number.isFinite(containerHeight) ||
    containerWidth <= 0 ||
    containerHeight <= 0 ||
    !Number.isFinite(sourceWidth) ||
    !Number.isFinite(sourceHeight) ||
    sourceWidth <= 0 ||
    sourceHeight <= 0
  ) {
    return { renderedWidth: Math.max(containerWidth, 0), renderedHeight: Math.max(containerHeight, 0), offsetX: 0, offsetY: 0 }
  }
  const sourceAspect = sourceWidth / sourceHeight
  const containerAspect = containerWidth / containerHeight
  if (sourceAspect > containerAspect) {
    const renderedWidth = containerWidth
    const renderedHeight = containerWidth / sourceAspect
    return { renderedWidth, renderedHeight, offsetX: 0, offsetY: (containerHeight - renderedHeight) / 2 }
  }
  const renderedHeight = containerHeight
  const renderedWidth = containerHeight * sourceAspect
  return { renderedWidth, renderedHeight, offsetX: (containerWidth - renderedWidth) / 2, offsetY: 0 }
}

/** Map one PC4 `normalized_2d` (top-left origin, y-down) joint into the
 * actual rendered `<video>` content rect — NOT the full SVG/element box. */
export function projectVideoAlignedJoint(
  joint: { x: number; y: number; confidence?: number },
  rect: ContainedMediaRect,
): { x: number; y: number; c: number } {
  return {
    x: rect.offsetX + Number(joint.x) * rect.renderedWidth,
    y: rect.offsetY + Number(joint.y) * rect.renderedHeight,
    c: Number(joint.confidence ?? 1),
  }
}

export function projectVideoAlignedFrame(
  frame: { joints?: Array<{ x: number; y: number; confidence?: number }> } | undefined,
  rect: ContainedMediaRect,
): Array<{ x: number; y: number; c: number }> {
  if (!frame?.joints?.length) return []
  return frame.joints.map((j) => projectVideoAlignedJoint(j, rect))
}

/** Sorted (monotonic, per Motion Asset contract) `timestamp_ms` samples. */
export function buildSortedTimestampsMs(frames: Array<{ timestamp_ms?: number | null }>): number[] {
  return frames.map((f) => Number(f.timestamp_ms ?? 0))
}

/**
 * Binary search: index of the timestamp nearest `targetMs`. O(log n).
 * Assumes `timestampsMs` is sorted ascending (Motion Asset contract:
 * `t_ms` strictly increasing). Ties resolve to the earlier sample.
 */
export function findNearestTimestampIndex(timestampsMs: number[], targetMs: number): number {
  const n = timestampsMs.length
  if (n === 0) return -1
  if (n === 1) return 0
  if (targetMs <= timestampsMs[0]) return 0
  if (targetMs >= timestampsMs[n - 1]) return n - 1

  let lo = 0
  let hi = n - 1
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (timestampsMs[mid] === targetMs) return mid
    if (timestampsMs[mid] < targetMs) lo = mid + 1
    else hi = mid
  }
  // `lo` is the first index whose timestamp is >= targetMs.
  const after = timestampsMs[lo]
  const before = timestampsMs[lo - 1]
  if (before === undefined) return lo
  const distAfter = Math.abs(after - targetMs)
  const distBefore = Math.abs(targetMs - before)
  return distAfter < distBefore ? lo : lo - 1
}

/**
 * Video clock is master (§5): resolve `video.currentTime` (seconds) to the
 * nearest Motion Asset frame index. Stateless — naturally correct across
 * `video.loop` wrap (currentTime resets near 0 → nearest resolves to the
 * first frame again, no separate wrap-detection state needed).
 */
export function findNearestMotionFrameByTime(
  frames: Array<{ timestamp_ms?: number | null }>,
  videoCurrentTimeSeconds: number,
): number {
  const timestampsMs = buildSortedTimestampsMs(frames)
  const targetMs = Math.max(0, videoCurrentTimeSeconds * 1000)
  return findNearestTimestampIndex(timestampsMs, targetMs)
}
